// Раздел «Предупреждения»: навигационные предупреждения NGA на своей карте.
//
// Устроен по образцу «Буёв»: своя карта, слой спрашивается ПО КАДРУ, рубежи —
// общим хуком. Дата — своя у раздела: по умолчанию «в силе сегодня», можно
// отступить в прошлое (история копится с подключения загрузчика).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import type { NavwarnCollection, NavwarnMessage } from '../api'
import { domainMaxBounds } from '../domain/limits'
import { useT } from '../i18n'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { useMapView } from '../map/mapView'
import { NAVWARN_FILL_LAYER, NAVWARN_HIT_LAYERS } from '../map/navwarn'
import { applyNavwarnLayers, navwarnAt, selectNavwarn } from '../map/navwarnLayer'
import { createStyleGate } from '../map/styleGate'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'
import { NavwarnCard } from './NavwarnCard'
import { NavwarnPanel } from './NavwarnPanel'
import {
  NAVWARN_URL_KEYS, filterCollection, messageBounds, parseNavwarnUrl, sameMsg,
  writeNavwarnUrl,
} from './navwarnState'
import type { MsgKey } from './navwarnState'

const EMPTY: NavwarnCollection = { type: 'FeatureCollection', features: [], messages: [] }

// Дальний Восток: Охотское и Японское моря, где стреляют чаще всего.
const HOME_CENTER: [number, number] = [146, 48]
const HOME_ZOOM = 3.6

/** Сутки UTC, как `today()` витрины и «сегодня» сервера. */
const utcToday = (): string => new Date().toISOString().slice(0, 10)

export function NavwarnPage() {
  const t = useT()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())

  const [searchParams, setSearchParams] = useSearchParams()
  const bootRef = useRef<ReturnType<typeof parseNavwarnUrl>>()
  if (!bootRef.current) bootRef.current = parseNavwarnUrl(searchParams)
  const boot = bootRef.current

  const { center, zoom, onMoveEnd } = useMapView()
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  const initialViewRef = useRef({ center: center ?? HOME_CENTER, zoom: zoom ?? HOME_ZOOM })

  const today = utcToday()
  const [date, setDate] = useState<string | null>(boot.date)
  const [kinds, setKinds] = useState<string[]>(boot.kinds)
  const [selected, setSelected] = useState<MsgKey | null>(boot.msg)
  const [eez, setEez] = useState(boot.eez)
  const [fd, setFd] = useState(boot.districts)
  const [box, setBox] = useState<ReturnType<typeof boundsToBox> | null>(null)

  // Единственный писатель раздела в URL; вид карты ведёт useMapView.
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const key of NAVWARN_URL_KEYS) next.delete(key)
      writeNavwarnUrl({ date, kinds, msg: selected, eez, districts: fd })
        .forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, kinds, selected, eez, fd])

  // Рубежи — ПОД зонами предупреждений: зона стрельб важнее линии ИЭЗ.
  const boundaries = { eez, districts: fd }
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box), beforeId: NAVWARN_FILL_LAYER, date,
  })

  const query = useQuery({
    queryKey: ['navwarn', box, date],
    queryFn: () => cabinetApi.navwarn({ bbox: box!, date }),
    enabled: box != null,
    // Пан не должен гасить список и слой до ответа: держим прошлый кадр.
    placeholderData: prev => prev,
    staleTime: 5 * 60_000,
  })
  const all = query.data ?? EMPTY
  const shown = useMemo(() => filterCollection(all, kinds), [all, kinds])

  const cardQuery = useQuery({
    queryKey: ['navwarn', 'msg', selected],
    queryFn: () => cabinetApi.navwarnMessage(selected!.navarea, selected!.year, selected!.number),
    enabled: selected != null,
  })

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      attributionControl: false,
      center: initialViewRef.current.center,
      zoom: initialViewRef.current.zoom,
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    const gate = createStyleGate()
    gateRef.current = gate

    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
    })

    map.on('load', () => {
      // Слои заводятся сразу, пустыми: рубежи ложатся ПОД них по `beforeId`.
      applyNavwarnLayers(map, { type: 'FeatureCollection', features: [] })
      for (const id of NAVWARN_HIT_LAYERS) {
        map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', id, () => { map.getCanvas().style.cursor = '' })
      }
      map.on('click', e => {
        const hit = navwarnAt(map, e.point)
        if (hit) setSelected({ navarea: hit.navarea, year: hit.year, number: hit.number })
      })
      setBox(boundsToBox(map))
      gate.markReady()
    })

    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Отобранная коллекция → источник, через шлюз стиля.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => applyNavwarnLayers(map, shown as unknown as GeoJSON.FeatureCollection))
  }, [shown])

  const selectedId = useMemo(
    () => all.messages.find(m => sameMsg(selected, m))?.id ?? cardQuery.data?.id ?? null,
    [all, selected, cardQuery.data],
  )
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => selectNavwarn(map, selectedId))
  }, [selectedId, shown])

  const onSelect = useCallback((m: NavwarnMessage) => {
    setSelected({ navarea: m.navarea, year: m.year, number: m.number })
    const map = mapRef.current
    const b = messageBounds(all, m.id)
    if (map && b) map.fitBounds(b, { padding: 120, maxZoom: 8, duration: 600 })
  }, [all])

  const onToggleKind = useCallback((kind: string | null) => {
    setKinds(prev => (kind == null ? [] : prev.includes(kind) ? prev.filter(k => k !== kind) : [...prev, kind]))
  }, [])

  const memoAsOf = useMemo(() => {
    const v = Object.values(all.meta?.memos ?? {}).filter(Boolean) as string[]
    return v.length ? v.sort()[0] : null
  }, [all])

  return (
    <div className="relative h-full w-full">
      {/* Высота, а НЕ `absolute inset-0` — см. mapContainer.test.ts. */}
      <div ref={containerRef} className="h-full w-full" />
      <BasemapAttribution corner="bl" />

      <NavwarnPanel
        date={date ?? today}
        today={today}
        onDate={setDate}
        messages={shown.messages}
        allMessages={all.messages}
        kinds={kinds}
        onToggleKind={onToggleKind}
        selected={selected}
        onSelect={onSelect}
        memoAsOf={memoAsOf}
        loading={query.isFetching}
        failed={query.isError}
        boundaries={boundaries}
        onToggleBoundary={key => (key === 'eez' ? setEez(v => !v) : setFd(v => !v))}
        boundaryMeta={boundaryMeta}
      />

      {selected && cardQuery.data && (
        <div className="pointer-events-none absolute right-3 top-3 z-10">
          <NavwarnCard data={cardQuery.data} onClose={() => setSelected(null)} />
        </div>
      )}
      {selected && cardQuery.isError && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 rounded-xl border border-hull bg-abyss/95 px-3 py-1 text-[12px] text-mist shadow-console">
          {t('navwarn.error')}
        </div>
      )}
    </div>
  )
}
